/**
 * Centralised registry of authorised external scripts for payment pages.
 *
 * This module maintains an allowlist of approved script URL patterns that can be
 * loaded on payment pages. All external scripts must be validated against this
 * registry before loading to ensure only authorised scripts are executed.
 *
 * @see SCRIPT_INVENTORY.md for detailed documentation of each script
 */

/**
 * Environment-specific URL patterns for authorised scripts.
 * Patterns use RegExp to allow for version flexibility while maintaining security.
 */
export const AUTHORISED_SCRIPT_PATTERNS = {
  /**
   * FreedomPay Hosted Payment Client (HPC) SDK
   * Used for PCI-compliant card payment processing via FreedomPay
   */
  freedomPay: {
    qa: /^https:\/\/hpc\.uat\.freedompay\.com\/api\/v[\d.]+\/cdn\/hpc_min\.js$/,
    prod: /^https:\/\/hpc\.freedompay\.com\/api\/v[\d.]+\/cdn\/hpc_min\.js$/,
  },

  /**
   * Google reCAPTCHA v3
   * Used for bot protection on payment forms
   */
  recaptcha: /^https:\/\/www\.google\.com\/recaptcha\/api\.js\?render=[a-zA-Z0-9_-]+$/,

  /**
   * Stripe.js
   * Used for PCI-compliant Stripe payment processing
   * Note: Loaded via @stripe/stripe-js npm package
   */
  stripe: /^https:\/\/js\.stripe\.com\/v\d+\/$/,

  /**
   * OpenComponents (OC) Client
   * Used for loading OC micro-frontend components
   */
  ocClient: {
    qa: /^https:\/\/mfe\.test\.workspace\.accessacloud\.com\/registry\/oc-client\/client\.js$/,
    prod: /^https:\/\/mfe\.workspace\.accessacloud\.com\/registry\/oc-client\/client\.js$/,
  },

  feedbackCollector: {
    qa: /^https:\/\/content\.mfe\.test\.workspace\.accessacloud\.com\/public\/components\/evo-feature-feedback-collector\/[\d.]+\/template\.js$/,
    prod: /^https:\/\/content\.mfe\.workspace\.accessacloud\.com\/public\/components\/evo-feature-feedback-collector\/[\d.]+\/template\.js$/,
  },

  /**
   * esm.sh CDN — React runtime
   * Used in the import map to resolve bare `react` specifier
   */
  esmReact: /^https:\/\/esm\.sh\/react@[\d.]+$/,

  /**
   * esm.sh CDN — ReactDOM client runtime
   * Used in the import map to resolve `react-dom/client` specifier
   */
  esmReactDomClient: /^https:\/\/esm\.sh\/react-dom@[\d.]+\/client$/,

  /**
   * esm.sh CDN — i18next internationalisation framework
   * Used in the import map to resolve bare `i18next` specifier
   */
  esmI18next: /^https:\/\/esm\.sh\/i18next@[\d.]+$/,

  /**
   * esm.sh CDN — react-i18next bindings
   * Used in the import map to resolve `react-i18next` specifier
   */
  esmReactI18next: /^https:\/\/esm\.sh\/react-i18next@[\d.]+\?external=react,i18next$/,

  /**
   * DMN Auth Components
   * Provides Google and Apple social sign-in on the booking form
   */
  authComponents: /^https:\/\/auth-components\.designmynight\.com\/[a-z0-9][a-z0-9.-]*\/dmn-auth-components\.js$/,

  /**
   * Google Tag Manager container
   * Container IDs are supplied by configuration or by the embedding partner
   */
  googleTagManager: /^https:\/\/www\.googletagmanager\.com\/gtm\.js\?id=GTM-[A-Z0-9]{4,20}$/,

  /**
   * Google Analytics 4 (gtag.js)
   * Measurement IDs are supplied by configuration or by the embedding partner
   */
  googleGtag: /^https:\/\/www\.googletagmanager\.com\/gtag\/js\?id=G-[A-Z0-9]{4,20}$/,
} as const;

export type ScriptType = keyof typeof AUTHORISED_SCRIPT_PATTERNS;

/**
 * Determines the current environment based on the URL or configuration.
 * Defaults to 'prod' for safety if environment cannot be determined.
 */
export function getCurrentEnvironment(): "qa" | "prod" {
  // Access window via globalThis so the file compiles under tsconfigs that
  // do not include DOM lib (e.g. tsconfig.vite.json).  At runtime, in the
  // browser globalThis.window === window; in Node it is undefined (or a test
  // mock set via `global.window`).
  const win = (globalThis as Record<string, unknown>).window as { location: { hostname: string } } | undefined;
  if (win?.location) {
    const hostname = win.location.hostname;
    if (
      hostname.includes("qa") ||
      /[-.]dev\./.test(hostname) ||
      hostname.startsWith("dev.") ||
      hostname.includes("localhost") ||
      hostname.includes("127.0.0.1") ||
      hostname.includes("uat") ||
      hostname.includes("playpen")
    ) {
      return "qa";
    }
  }
  return "prod";
}

/**
 * Validates whether a script URL is authorised for loading.
 *
 * @param url - The script URL to validate
 * @param scriptType - The type of script being validated
 * @returns true if the script is authorised, false otherwise
 */
export function isAuthorisedScript(url: string, scriptType: ScriptType): boolean {
  if (!url || typeof url !== "string") {
    return false;
  }

  const pattern = AUTHORISED_SCRIPT_PATTERNS[scriptType];

  if (!pattern) {
    console.error(`[ScriptSecurity] Unknown script type: ${scriptType}`);
    return false;
  }

  // Handle environment-specific patterns (like FreedomPay)
  if (typeof pattern === "object" && "qa" in pattern && "prod" in pattern) {
    const env = getCurrentEnvironment();
    const envPattern = pattern[env];
    const isAuthorised = envPattern.test(url);

    if (!isAuthorised) {
      console.warn(`[ScriptSecurity] Unauthorised ${scriptType} script URL for ${env} environment: ${url}`);
    }

    return isAuthorised;
  }

  // Handle single pattern (like recaptcha, stripe)
  if (pattern instanceof RegExp) {
    const isAuthorised = pattern.test(url);

    if (!isAuthorised) {
      console.warn(`[ScriptSecurity] Unauthorised ${scriptType} script URL: ${url}`);
    }

    return isAuthorised;
  }

  return false;
}

/**
 * Validates a FreedomPay URL specifically, with environment awareness.
 *
 * @param url - The FreedomPay script URL to validate
 * @returns true if the URL is authorised for the current environment
 */
export function isAuthorisedFreedomPayUrl(url: string): boolean {
  return isAuthorisedScript(url, "freedomPay");
}

/**
 * Validates a reCAPTCHA URL.
 *
 * @param url - The reCAPTCHA script URL to validate
 * @returns true if the URL matches the expected Google reCAPTCHA pattern
 */
export function isAuthorisedRecaptchaUrl(url: string): boolean {
  return isAuthorisedScript(url, "recaptcha");
}

/**
 * Validates a Stripe.js URL.
 *
 * @param url - The Stripe script URL to validate
 * @returns true if the URL matches the expected Stripe.js pattern
 */
export function isAuthorisedStripeUrl(url: string): boolean {
  return isAuthorisedScript(url, "stripe");
}

/**
 * Validates an OC Client URL, with environment awareness.
 *
 * @param url - The OC Client script URL to validate
 * @returns true if the URL is authorised for the current environment
 */
export function isAuthorisedOcClientUrl(url: string): boolean {
  return isAuthorisedScript(url, "ocClient");
}

export function isAuthorisedFeedbackCollectorUrl(url: string): boolean {
  return isAuthorisedScript(url, "feedbackCollector");
}

/**
 * Validates an esm.sh import map URL.
 *
 * @param url - The esm.sh URL to validate
 * @param scriptType - The specific esm.sh script type
 * @returns true if the URL is authorised
 */
export function isAuthorisedEsmUrl(url: string, scriptType: ScriptType): boolean {
  return isAuthorisedScript(url, scriptType);
}

/**
 * Validates a DMN Auth Components URL.
 *
 * @param url - The auth components script URL to validate
 * @returns true if the URL matches the expected auth components pattern
 */
export function isAuthorisedAuthComponentsUrl(url: string): boolean {
  return isAuthorisedScript(url, "authComponents");
}

/**
 * Validates a Google Tag Manager container URL.
 *
 * @param url - The GTM script URL to validate
 * @returns true if the URL matches the expected GTM container pattern
 */
export function isAuthorisedGoogleTagManagerUrl(url: string): boolean {
  return isAuthorisedScript(url, "googleTagManager");
}

/**
 * Validates a Google Analytics 4 gtag.js URL.
 *
 * @param url - The gtag.js script URL to validate
 * @returns true if the URL matches the expected gtag.js pattern
 */
export function isAuthorisedGtagUrl(url: string): boolean {
  return isAuthorisedScript(url, "googleGtag");
}
